import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ColorPicker, useBuiltInColorMap } from '@atlassian/color-picker';
import { CollapsibleItem, useProductDependencies } from '@atlassian/company-hub-common';
import { resolveAdfColorForPicker } from '@atlassian/company-hub-common/hub-brand-palette';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';

import type { LinksMenuExtensionActions } from '../../../../controllers/use-links-menu-parameters-and-actions';
import type { LinksMenuParameters } from '../../../../types';
import { messages } from './messages';

export type TextConfigProps = Pick<LinksMenuParameters['styles'], 'highlightColor' | 'textColor'> &
	Pick<LinksMenuExtensionActions, 'updateHighlightColor' | 'updateTextColor'> & {
		isReadableTextColor: (color: string) => boolean;
	};

export const TextConfig = ({
	highlightColor,
	updateHighlightColor,
	textColor,
	updateTextColor,
	isReadableTextColor,
}: TextConfigProps): React.JSX.Element => {
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette } = branding ?? {};
	const { highlightColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);
	const { formatMessage } = useIntl();

	// Advanced-theming opt-in: when gate ON, swap to the 22-shade brand
	// palette and translate hex <-> ADF var-ref storage. Gate OFF or
	// non-help-center hosts (e.g. Confluence Company Hub which doesn't
	// mount the provider) take the legacy passthrough branch unchanged.
	const theming = useHubTheming();
	const isAdvancedThemingEnabled = theming?.isAdvancedThemingEnabled === true;
	const palette = isAdvancedThemingEnabled ? theming?.palette : undefined;
	const primaryColor = isAdvancedThemingEnabled ? theming?.primaryColor : undefined;
	const v2BgMap = isAdvancedThemingEnabled ? theming?.brandBackgroundColorMap : undefined;

	// Text + highlight are always populated (manifest seeds a WCAG-readable
	// AK token, and useAutoTransitioningColors keeps text in sync on bg
	// change), so the fallback ref isn't hit in practice — it's only used
	// by resolveAdfColorForPicker if the stored value is null. Pass
	// primary.1000 as a sensible visible default (combo of primary.200 bg).
	const textPickerValue = palette
		? resolveAdfColorForPicker(textColor, palette, 'primary.1000', primaryColor)
		: textColor;
	const highlightPickerValue = palette
		? resolveAdfColorForPicker(highlightColor, palette, 'primary.1000', primaryColor)
		: highlightColor;

	const toStoredColor = isAdvancedThemingEnabled ? theming?.toStoredColor : undefined;

	const handleTextColorChange = (color: string) => {
		updateTextColor(toStoredColor ? toStoredColor(color) : color);
	};
	const handleHighlightColorChange = (color: string) => {
		updateHighlightColor(toStoredColor ? toStoredColor(color) : color);
	};

	return (
		<CollapsibleItem title={formatMessage(messages.textTitle)}>
			<Stack space="space.200">
				<Inline spread="space-between" alignBlock="center">
					<Text color="color.text">
						<FormattedMessage {...messages.textColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigTextColor"
						builtInColorMap={v2BgMap ?? (hideDefaultColorPalette ? emptyColorMap : textColorMap)}
						isReadableColor={isReadableTextColor}
						onChange={handleTextColorChange}
						value={textPickerValue}
						pickerTitle={messages.textColor}
						showCustomPalette
						showTextIcon
					/>
				</Inline>
				<Inline spread="space-between" alignBlock="center">
					<Text color="color.text">
						<FormattedMessage {...messages.selectedStateColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigHighlightColor"
						builtInColorMap={
							v2BgMap ?? (hideDefaultColorPalette ? emptyColorMap : highlightColorMap)
						}
						onChange={handleHighlightColorChange}
						value={highlightPickerValue}
						pickerTitle={messages.selectedStateColor}
						showCustomPalette
					/>
				</Inline>
			</Stack>
		</CollapsibleItem>
	);
};
